import { useEffect, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { GET_STORE_TIP_SETTINGS } from "src/constants/queries";
import { UPDATE_STORE_TIP_SETTINGS } from "src/constants/mutations";
import { successToast, failureToast } from "src/utils/toasts";
import { BtnLoader } from "src/Loader";
import FormRowVertical from "src/components/common/FormRowVertical";
import Input, { InputTypeEnum } from "src/components/common/Input";
import { StoreTipSettings, UpdateStoreTipSettingsInput } from "src/interfaces/tipSettings.interface";

const PARTY_SIZE_THRESHOLD = { min: 1, max: 20, default: 6 };
const TIP_PERCENTAGE = { min: 0, max: 100, default: 18 };

const DineInAutoTipSettings = () => {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [partySizeThresholdStr, setPartySizeThresholdStr] = useState<string>(String(PARTY_SIZE_THRESHOLD.default));
  const [tipPercentageStr, setTipPercentageStr] = useState<string>(String(TIP_PERCENTAGE.default));
  const [errors, setErrors] = useState<{ partySizeThreshold?: string; tipPercentage?: string }>({});

  useEffect(() => {
    fetchConfig();
  }, [hasRestaurant?.correlationId]);

  const fetchConfig = async () => {
    if (!hasRestaurant?.correlationId) return;

    setIsLoadingConfig(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_STORE_TIP_SETTINGS,
        variables: {
          retrieveStoresInput: {
            storeCorrelationIds: [hasRestaurant.correlationId],
          },
        },
      });

      if (data?.errors?.length) {
        throw data.errors;
      }

      const stores = data?.data?.stores || [];
      const storeData: StoreTipSettings | null = stores.length > 0 ? stores[0] : null;
      if (storeData?.correlationId) {
        const threshold = storeData.dineInAutoTipPartySizeThreshold ?? PARTY_SIZE_THRESHOLD.default;
        const pct = storeData.dineInAutoTipPercentage ?? TIP_PERCENTAGE.default;
        setPartySizeThresholdStr(String(threshold));
        setTipPercentageStr(String(pct));
      }
    } catch (error: unknown) {
      console.error("Error fetching dine-in auto-tip config:", error);
      const err = error as { message?: string; [0]?: { message?: string } };
      const message = err?.message || err?.[0]?.message || "Failed to load dine-in auto-tip settings";
      failureToast(message);
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { partySizeThreshold?: string; tipPercentage?: string } = {};
    const thresholdNum = partySizeThresholdStr === "" ? NaN : parseInt(partySizeThresholdStr, 10);
    if (
      Number.isNaN(thresholdNum) ||
      thresholdNum < PARTY_SIZE_THRESHOLD.min ||
      thresholdNum > PARTY_SIZE_THRESHOLD.max
    ) {
      newErrors.partySizeThreshold = `Party size threshold must be between ${PARTY_SIZE_THRESHOLD.min} and ${PARTY_SIZE_THRESHOLD.max}`;
    }
    const pctNum = tipPercentageStr === "" ? NaN : parseFloat(tipPercentageStr);
    if (Number.isNaN(pctNum) || pctNum < TIP_PERCENTAGE.min || pctNum > TIP_PERCENTAGE.max) {
      newErrors.tipPercentage = `Tip percentage must be between ${TIP_PERCENTAGE.min}% and ${TIP_PERCENTAGE.max}%`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!hasRestaurant?.correlationId) {
      failureToast("No restaurant selected");
      return;
    }
    if (!validateForm()) {
      failureToast("Please fix validation errors");
      return;
    }

    const thresholdNum = parseInt(partySizeThresholdStr, 10);
    const pctNum = parseFloat(tipPercentageStr);
    setLoading(true);
    try {
      const input: UpdateStoreTipSettingsInput = {
        storeCorrelationId: hasRestaurant.correlationId,
        dineInAutoTipPartySizeThreshold: thresholdNum,
        dineInAutoTipPercentage: pctNum,
      };

      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_STORE_TIP_SETTINGS,
        variables: {
          updateStoreTipSettingsInput: input,
        },
      });

      if (data?.errors?.length) {
        throw data.errors;
      }

      successToast("Dine-in auto-tip settings saved successfully!");
      await fetchConfig();
    } catch (error: unknown) {
      console.error("Error saving dine-in auto-tip config:", error);
      const err = error as { message?: string; [0]?: { message?: string } };
      const message = err?.message || err?.[0]?.message || "Failed to save dine-in auto-tip settings";
      failureToast(message);
    } finally {
      setLoading(false);
    }
  };

  if (isLoadingConfig) {
    return (
      <div className="bg-[#ffffff] rounded-[16px] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)] flex justify-center items-center py-12">
        <BtnLoader />
      </div>
    );
  }

  return (
    <div className="bg-[#ffffff] rounded-[16px] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]">
      <h2 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em] mb-2">Dine-in auto-tip</h2>
      <p className="text-[14px] text-[#6b7280] mb-6">
        When party size is greater than the threshold below, this tip percentage is applied automatically to dine-in orders (party size is set in the desktop app).
      </p>

      <div className="space-y-4">
          <FormRowVertical
            label={`Add auto-tip when party size is greater than (${PARTY_SIZE_THRESHOLD.min}-${PARTY_SIZE_THRESHOLD.max})`}
          >
            <Input
              name="partySizeThreshold"
              type={InputTypeEnum.NUMBER}
              value={partySizeThresholdStr}
              onChange={(e) => {
                setPartySizeThresholdStr(e.target.value);
                if (errors.partySizeThreshold) {
                  setErrors((prev) => ({ ...prev, partySizeThreshold: undefined }));
                }
              }}
              min={String(PARTY_SIZE_THRESHOLD.min)}
              max={String(PARTY_SIZE_THRESHOLD.max)}
              className="w-32"
            />
            {errors.partySizeThreshold && (
              <p className="text-red-500 text-xs mt-1">{errors.partySizeThreshold}</p>
            )}
          </FormRowVertical>

          <FormRowVertical label={`Tip percentage to add (%) (${TIP_PERCENTAGE.min}-${TIP_PERCENTAGE.max})`}>
            <div className="flex items-center gap-2">
              <Input
                name="tipPercentage"
                type={InputTypeEnum.NUMBER}
                value={tipPercentageStr}
                onChange={(e) => {
                  setTipPercentageStr(e.target.value);
                  if (errors.tipPercentage) {
                    setErrors((prev) => ({ ...prev, tipPercentage: undefined }));
                  }
                }}
                min={String(TIP_PERCENTAGE.min)}
                max={String(TIP_PERCENTAGE.max)}
                className="w-32"
              />
              <span className="text-gray-600">%</span>
            </div>
            {errors.tipPercentage && (
              <p className="text-red-500 text-xs mt-1">{errors.tipPercentage}</p>
            )}
          </FormRowVertical>

        <div className="flex justify-end mt-6">
          {loading ? (
            <BtnLoader />
          ) : (
            <button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className="px-5 py-2.5 rounded-xl bg-[#FD7657] text-white text-sm font-semibold hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20 focus:ring-offset-2 disabled:opacity-50"
            >
              Save
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DineInAutoTipSettings;
